import { createContext, useContext, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { offerSourcingGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerSourcingEditRight'
import { selectOfferDetail, selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { OfferBookingStatus, RoleInOffer, TenderDocumentStatus } from '@eapproval/offer-shared'

const CostBudgetAccessContext = createContext(null)

export const CostBudgetAccessProvider = ({ children, costBudgetIndex, hasSelectedSubSystem = false }) => {
  const offerPermissions = useSelector(selectOfferPermissions)
  const { role } = useSelector(selectOfferDetail)

  const status = useWatch({ name: 'status' })
  const costBudgetDetail = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail` })
  const splitForm = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.splitForm` }) ?? ''

  const tenderDocuments = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments` }) ?? []
  const vendorDetails = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.subcontractors` }) ?? []
  const requireProcurementOfficer =
    useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.requireProcurementOfficer` }) ?? false
  const workflowIssues = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.workflow.issues` }) ?? []

  const isParentCostBudget = splitForm === ''
  const value = useMemo(() => {
    const isCostBudgetActive = costBudgetDetail?.isActive ?? false
    const hasVendorUploads = vendorDetails.flatMap((vendor) => vendor?.history || []).length > 0

    const hasRoleToEditCostBudget = offerPermissions[offerSourcingGroupPolicyType.SOURCING_COST_BUDGET]?.edit ?? false
    const hasRoleToEditPM040Procurement =
      offerPermissions[offerSourcingGroupPolicyType.SOURCING_PM040_PROCUREMENT]?.edit ?? false
    const hasRoleToEditPM040Technical =
      offerPermissions[offerSourcingGroupPolicyType.SOURCING_PM040_TECHNICAL]?.edit ?? false
    const hasRoleToEditPM100Technical =
      offerPermissions[offerSourcingGroupPolicyType.SOURCING_PM100_TECHNICAL]?.edit ?? false

    const canEditCostBudget = hasRoleToEditCostBudget && hasSelectedSubSystem && isCostBudgetActive
    const canEditCostBudgetType =
      canEditCostBudget && tenderDocuments.every((tenderDoc) => tenderDoc.status !== TenderDocumentStatus.POSTED)

    return {
      canEditCostBudgetSubSystem: hasRoleToEditCostBudget && isCostBudgetActive,
      canEditCostBudget,
      canEditCostBudgetType,
      canEditGeneralInfo: canEditCostBudget,
      canEditInternalDocuments: canEditCostBudget,
      canEditTenderDocuments: canEditCostBudget,
      canAddTenderDocument:
        canEditCostBudget && tenderDocuments.every((tenderDoc) => tenderDoc.status === TenderDocumentStatus.POSTED),
      // Stay visible when the offer is locked or the cost budget is inactive; the modal turns read-only instead
      canOpenWorkflowChat: requireProcurementOfficer && workflowIssues.length > 0,
      canActionWorkflowChat: canEditCostBudget && requireProcurementOfficer && workflowIssues.length > 0,
      canEditPM040Procurement: hasRoleToEditPM040Procurement && isCostBudgetActive && hasVendorUploads,
      canEditPM040Technical:
        hasRoleToEditPM040Technical && isCostBudgetActive && hasVendorUploads && hasSelectedSubSystem,
      canEditPM100Technical:
        hasRoleToEditPM100Technical && isCostBudgetActive && hasVendorUploads && hasSelectedSubSystem,
      hasSelectedSubSystem,
      hasVendorUploads,
      showPM040: isParentCostBudget,
      showPM100: Object.values(OfferBookingStatus).includes(status),
      isCostBudgetActive,
      canEditVendor: requireProcurementOfficer
        ? canEditCostBudget && role === RoleInOffer.PROCUREMENT_OFFICER
        : canEditCostBudget,
    }
  }, [
    costBudgetDetail?.isActive,
    hasSelectedSubSystem,
    offerPermissions,
    requireProcurementOfficer,
    status,
    tenderDocuments,
    vendorDetails,
    workflowIssues.length,
  ])

  return <CostBudgetAccessContext.Provider value={value}>{children}</CostBudgetAccessContext.Provider>
}

export const useCostBudgetAccess = () => {
  const context = useContext(CostBudgetAccessContext)

  if (!context) {
    throw new Error('useCostBudgetAccess must be used within CostBudgetAccessProvider')
  }

  return context
}
